/**
 * WhatsApp-link personalization: a link like /pages/wardrobe-builder?order=SLAY-169672
 * carries no login, so this resolves the order's size/category via a small lookup
 * endpoint and writes them into the visitor's (login-free) cart attributes. A full
 * page reload would then be enough for Liquid to read cart.attributes server-side —
 * but that's a second full navigation (re-downloading/re-parsing every theme asset)
 * on top of the lookup call itself, and that combined latency was measured as the
 * likely cause of an very fast bounce rate on real WhatsApp clicks. Instead, this
 * fetches just the section's re-rendered HTML via Shopify's Section Rendering API
 * (?section_id=) — same correct server-rendered Liquid output, reflecting the cart
 * attributes just written, but as one small AJAX call instead of a full navigation.
 */
/**
 * Fetches the section's re-rendered HTML (reflecting whatever was just
 * written to cart.attributes) via Shopify's Section Rendering API and swaps
 * it in without a full page navigation — see file header for why. Falls
 * back to a full reload if anything about the fetch/parse/swap goes wrong;
 * cart attributes are already set by the time this is called, so a reload
 * still renders correctly, just slower. Shared by both the ?order= flow and
 * the ?category= + size-modal flow below.
 */
function swapWardrobeSection() {
  var oldEl = document.querySelector('wardrobe-builder');
  if (!oldEl) return Promise.reject(new Error('wardrobe-builder element not found'));

  /* Must be the FULLY QUALIFIED section id (e.g. "template--123__wardrobe-builder"),
     not just "wardrobe-builder" — for a section that's part of a JSON template,
     an unqualified section_id renders the section but with section.blocks
     empty, silently dropping every category rail except Recommended For You.
     data-section-id carries the correct value from the current (real) render. */
  var sectionId = oldEl.dataset.sectionId;
  if (!sectionId) return Promise.reject(new Error('data-section-id missing'));

  return fetch(window.location.pathname + '?section_id=' + encodeURIComponent(sectionId))
    .then(function (res) {
      return res.ok ? res.text() : null;
    })
    .then(function (html) {
      if (!html) throw new Error('section fetch failed');
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var newEl = doc.querySelector('wardrobe-builder');
      var liveOldEl = document.querySelector('wardrobe-builder');
      if (!newEl || !liveOldEl) throw new Error('wardrobe-builder element missing from response');
      // Inserting newEl into the live document upgrades it (customElements.define
      // already ran on first load), synchronously firing its connectedCallback —
      // no extra init call needed here.
      liveOldEl.replaceWith(newEl);
      history.replaceState(null, '', window.location.pathname + window.location.hash);
    })
    .catch(function (swapError) {
      console.warn('Wardrobe section swap failed, falling back to reload', swapError);
      window.location.replace(window.location.pathname + window.location.hash);
    });
}

(function resolveOrderPersonalization() {
  var params = new URLSearchParams(window.location.search);
  var orderParam = params.get('order');
  if (!orderParam) return;

  /* The inline synchronous script earlier in this section already hid the
     page (html.wb-personalizing) before first paint, so there's no generic
     content to flash. reveal() is called once the correct content is in
     place (section swapped in), or on any path that can't produce correct
     personalized content (no data found, or an error). */
  var revealed = false;
  function reveal() {
    if (revealed) return;
    revealed = true;
    document.documentElement.classList.remove('wb-personalizing');
  }
  // Safety net: never leave the customer stuck behind the loading overlay
  // indefinitely if something hangs.
  var revealTimeout = setTimeout(reveal, 6000);

  var LOOKUP_URL = 'https://f3dashboard.vercel.app/api/wardrobe-lookup?order=' + encodeURIComponent(orderParam);

  fetch(LOOKUP_URL)
    .then(function (res) {
      return res.ok ? res.json() : null;
    })
    .then(function (data) {
      if (!data || (!data.size && !data.category)) {
        if (typeof mixpanel !== 'undefined') {
          mixpanel.track('Wardrobe Order Lookup No Data', { order: orderParam });
        }
        clearTimeout(revealTimeout);
        reveal();
        return null;
      }

      if (typeof mixpanel !== 'undefined') {
        mixpanel.track('Wardrobe Order Lookup Succeeded', {
          order: orderParam,
          size: data.size || null,
          category: data.category || null,
        });
      }

      var attributes = {};
      if (data.size) attributes.wb_size = data.size;
      if (data.category) attributes.wb_category = data.category;

      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attributes }),
      });
    })
    .then(function (cartUpdateResponse) {
      if (!cartUpdateResponse) return;
      // Cart attributes are now correctly set — swap the section in.
      return swapWardrobeSection().then(function () {
        clearTimeout(revealTimeout);
        reveal();
      });
    })
    .catch(function (e) {
      console.warn('Wardrobe order personalization failed, showing defaults instead', e);
      if (typeof mixpanel !== 'undefined') {
        mixpanel.track('Wardrobe Order Lookup Failed', { order: orderParam });
      }
      clearTimeout(revealTimeout);
      reveal();
    });
})();

/**
 * ?category=Oxford link personalization: simpler than the ?order= flow above
 * since the category is already known from the URL — no lookup call needed,
 * it's just written straight to cart.attributes. Size still isn't known for
 * a fresh guest though, so this shows a one-time "Select Your Size" modal
 * (only if we don't already have a size from a customer metafield or an
 * earlier visit) instead of guessing one.
 */
(function resolveCategoryAndSize() {
  var wardrobeEl = document.querySelector('wardrobe-builder');
  if (!wardrobeEl) return;

  var params = new URLSearchParams(window.location.search);
  var categoryParam = params.get('category');
  var orderParam = params.get('order');
  if (orderParam) return; // resolveOrderPersonalization above already owns this load

  var modal = document.querySelector('[data-wb-onboarding]');

  // The category label needs no network call — it's already in the URL —
  // so fill in the modal's copy immediately, before it's even shown.
  if (categoryParam && modal) {
    var subtitleEl = modal.querySelector('[data-wb-onboarding-subtitle]');
    if (subtitleEl && subtitleEl.dataset.categoryTemplate) {
      subtitleEl.textContent = subtitleEl.dataset.categoryTemplate.replace('{category}', categoryParam);
    }
  }

  var revealed = !categoryParam; // nothing hid the page if there's no category swap to wait for
  function reveal() {
    if (revealed) return;
    revealed = true;
    document.documentElement.classList.remove('wb-personalizing');
  }
  var revealTimeout = categoryParam ? setTimeout(reveal, 6000) : null;

  function showSizeModalIfNeeded() {
    if (!modal) return;
    // Re-query rather than reuse the wardrobeEl closed over above — a
    // category swap may have already replaced that element with a fresh
    // one (swapWardrobeSection does oldEl.replaceWith(newEl)), and the
    // stale reference would read data-size-known off a detached node.
    var currentWardrobeEl = document.querySelector('wardrobe-builder');
    var sizeKnown = currentWardrobeEl && currentWardrobeEl.dataset.sizeKnown === 'true';
    if (sizeKnown) return;

    modal.hidden = false;
    if (currentWardrobeEl) currentWardrobeEl.classList.add('wb-content-blurred');
    if (typeof mixpanel !== 'undefined') {
      mixpanel.track('Wardrobe Onboarding Modal Shown', { category: categoryParam || null });
    }

    function closeModal() {
      modal.hidden = true;
      // Re-query rather than reuse currentWardrobeEl — a successful size
      // pick swaps in a brand new element, so the blur class needs to come
      // off whichever <wardrobe-builder> is live right now, not the one
      // captured when the modal opened.
      var liveEl = document.querySelector('wardrobe-builder');
      if (liveEl) liveEl.classList.remove('wb-content-blurred');
    }

    modal.querySelectorAll('[data-wb-onboarding-dismiss]').forEach(function (el) {
      el.addEventListener('click', closeModal);
    });

    modal.querySelectorAll('[data-wb-onboarding-size]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var size = btn.dataset.wbOnboardingSize;
        modal.querySelectorAll('[data-wb-onboarding-size]').forEach(function (b) {
          b.classList.toggle('is-selected', b === btn);
          // Loading spinner only on the tapped button — the write + swap
          // takes ~1-2s and this is what tells the customer their tap
          // registered and something's happening, not just "nothing moved".
          b.classList.toggle('is-loading', b === btn);
          b.disabled = true;
        });

        if (typeof mixpanel !== 'undefined') {
          mixpanel.track('Wardrobe Onboarding Size Selected', { size: size, category: categoryParam || null });
        }

        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: { wb_size: size } }),
        })
          .then(function () {
            return swapWardrobeSection();
          })
          .then(closeModal)
          .catch(function (e) {
            console.warn('Wardrobe onboarding size selection failed', e);
            closeModal();
          });
      });
    });
  }

  if (categoryParam) {
    if (typeof mixpanel !== 'undefined') {
      mixpanel.track('Wardrobe Category Link Opened', { category: categoryParam });
    }
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { wb_category: categoryParam } }),
    })
      .then(function () {
        return swapWardrobeSection();
      })
      .then(function () {
        clearTimeout(revealTimeout);
        reveal();
        // showSizeModalIfNeeded();
      })
      .catch(function (e) {
        console.warn('Wardrobe category personalization failed, showing defaults instead', e);
        clearTimeout(revealTimeout);
        reveal();
        // showSizeModalIfNeeded();
      });
  } else {
    // showSizeModalIfNeeded();
  }
})();

/**
 * Checkout on this page goes through GoKwik (same as cart/PDP elsewhere in this
 * theme), not native Shopify checkout. WardrobeBuilder.goToCheckout() adds the 3
 * selected shirts to the real cart and reloads with ?wb_checkout=1 — GoKwik reads
 * a page-load snapshot of the cart (window.cart, set in snippets/gokwik.liquid),
 * so the reload is required for it to see the items that were just added. This
 * IIFE then waits for GoKwik's own "gokwikLoaded" ready signal and clicks the
 * hidden real GoKwik button so its own script takes over the redirect.
 */
(function autoTriggerGokwikCheckout() {
  var params = new URLSearchParams(window.location.search);
  if (!params.has('wb_checkout')) return;

  // Clean the URL without a further reload — this load's cart snapshot is the
  // one we want GoKwik to read.
  window.history.replaceState({}, '', window.location.pathname + window.location.hash);

  var clicked = false;

  function clickGokwikButton() {
    var trigger = document.querySelector('[data-wb-gokwik-trigger] .gokwik-checkout button');
    if (trigger && !trigger.disabled) {
      clicked = true;
      trigger.click();
      return true;
    }
    return false;
  }

  if (clickGokwikButton()) return;

  window.addEventListener('gokwikLoaded', clickGokwikButton);

  // Fallback: if GoKwik never signals ready (blocked script, slow network),
  // fall back to native Shopify checkout rather than stranding the customer
  // on a dead button.
  setTimeout(function () {
    if (!clicked && !clickGokwikButton()) {
      if (typeof mixpanel !== 'undefined') {
        mixpanel.track('Wardrobe GoKwik Handoff Failed');
      }
      window.location.href = '/checkout';
    }
  }, 6000);
})();

/**
 * <wardrobe-builder>
 * Personalized post-purchase wardrobe builder: tracks up to `goal` selected
 * shirts client-side, drives the sticky progress bar/drawer, and hands the
 * selection off to the real Shopify cart at checkout time.
 */
class WardrobeBuilder extends HTMLElement {
  connectedCallback() {
    this.goal = parseInt(this.dataset.goal, 10) || 3;
    this.discountCode = this.dataset.discountCode || '';
    /* Per-shirt rate, not a fixed total — the goal is a MINIMUM to unlock any
       reward, not a cap. Total discount is always discountPerShirt × however
       many shirts are actually selected, so it scales naturally past 3 (3=₹300,
       4=₹400, ...). This must match discountCode's actual per-item amount in
       Shopify Admin (an "Amount off products" code discount with a minimum
       quantity), so the displayed number can never drift from what's charged. */
    this.discountPerShirt = parseFloat(this.dataset.discountPerShirt) || 0;
    this.upiDiscountPerShirt = parseFloat(this.dataset.upiDiscountPerShirt) || 0;
    this.storageKey = `wb-state-${this.dataset.storageScope || 'default'}`;
    this.completionTracked = false;

    this.state = new Map();
    /* Per-productId promise chain so a rapid add-then-remove (or vice versa)
       on the same product always resolves against the real cart in the
       order the customer actually clicked, instead of racing. */
    this.cartSyncQueue = new Map();

    this.slots = Array.from(this.querySelectorAll('[data-wb-slot]'));
    this.slotsExtra = this.querySelector('[data-wb-slots-extra]');
    this.countLabel = this.querySelector('[data-wb-count]');
    this.teaser = this.querySelector('[data-wb-teaser]');
    this.drawerItems = this.querySelector('[data-wb-drawer-items]');
    this.checkoutBtn = this.querySelector('[data-wb-checkout]');
    this.celebrationDiscountLabel = this.querySelector('[data-wb-celebration-discount]');

    this.restoreState();
    this.persistState();
    this.track('Wardrobe Page Viewed', {
      default_size: this.querySelector('[data-wb-badge-size]')?.textContent || null,
      initial_count: this.state.size,
    });
    this.addEventListener('click', this.onClick.bind(this));
    /* Bound references kept on `this` (not inline) so disconnectedCallback can
       remove exactly these document/window listeners — needed now that the
       WhatsApp-link personalization flow (see resolveOrderPersonalization
       above) replaces this element with a freshly-rendered one instead of
       reloading the page; without cleanup, the replaced-out instance's
       document/window listeners would stay attached forever. */
    this._onDocumentClick = this.onDocumentClick.bind(this);
    document.addEventListener('click', this._onDocumentClick);
    /* The hero banner is a div[role="button"], not a native <button> (its
       content includes block-level children a real button can't validly
       contain), so Enter/Space activation has to be wired up manually here —
       real buttons get that for free, divs don't. */
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const heroScroll = event.target.closest('[data-wb-hero-scroll]');
      if (!heroScroll) return;
      event.preventDefault();
      this.scrollToProducts();
    });
    /* Browser back/forward can restore this page from a frozen snapshot (bfcache)
       instead of reloading — if the customer left mid-checkout, the button stays
       stuck showing "Adding to cart…" / disabled, since no JS re-runs on restore.
       event.persisted === true is how you detect that case; re-render to fix it. */
    this._onPageshow = (event) => {
      if (!event.persisted) return;
      if (this.checkoutBtn) this.checkoutBtn.textContent = 'Continue to Checkout';
      this.restoreState();
      this.render();
    };
    window.addEventListener('pageshow', this._onPageshow);
    this.render();

    const pendingAdd = sessionStorage.getItem('wb-pending-add');
    if (pendingAdd && this.dataset.sizeKnown === 'true') {
      sessionStorage.removeItem('wb-pending-add');
      const btn = this.querySelector(`[data-wb-add][data-product-id="${pendingAdd}"]`);
      if (btn && !this.state.has(pendingAdd)) {
        // slight delay to let rendering finish
        setTimeout(() => this.toggleProduct(btn), 50);
      }
    }

    this.stickyBar = this.querySelector('.wb-sticky');
    this._onScroll = () => {
      if (window.scrollY > 50) {
        this.stickyBar?.classList.add('is-visible');
      } else {
        this.stickyBar?.classList.remove('is-visible');
      }
    };
    window.addEventListener('scroll', this._onScroll, { passive: true });
    this._onScroll();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocumentClick);
    window.removeEventListener('pageshow', this._onPageshow);
    window.removeEventListener('scroll', this._onScroll);
  }

  track(eventName, props) {
    if (typeof mixpanel === 'undefined') return;
    mixpanel.track(eventName, props);
  }

  adjustPopoverPosition(popover) {
    popover.style.left = '50%';
    popover.style.transform = 'translateX(-50%)';
    
    setTimeout(() => {
      const rect = popover.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const offsetLeft = rect.left;
      const offsetRight = viewportWidth - rect.right;
      
      if (offsetLeft < 10) {
        const adjustment = 10 - offsetLeft;
        popover.style.left = `calc(50% + ${adjustment}px)`;
      } else if (offsetRight < 10) {
        const adjustment = 10 - offsetRight;
        popover.style.left = `calc(50% - ${adjustment}px)`;
      }
    }, 0);
  }

  onClick(event) {
    const heroScroll = event.target.closest('[data-wb-hero-scroll]');
    if (heroScroll) return this.scrollToProducts();

    /* Tracked but NOT intercepted — the link navigates normally to the PDP.
       Pairs with "Wardrobe Page Viewed" (fired on every load of this page) to
       build a Mixpanel funnel: how many people who clicked through to a
       product never came back to the wardrobe page. No client-side "did they
       return" logic needed — Mixpanel computes that from the same visitor
       doing step 1 then never doing step 2. */
    const productLink = event.target.closest('[data-wb-product-card] a[href]');
    if (productLink) {
      const card = productLink.closest('[data-wb-product-card]');
      const cardAddBtn = card?.querySelector('[data-wb-add]');
      this.track('Wardrobe Product Clicked', {
        product_id: cardAddBtn?.dataset.productId || null,
        product_title: cardAddBtn?.dataset.title || null,
      });
    }

    const addBtn = event.target.closest('[data-wb-add]');
    if (addBtn) {
      if (this.dataset.sizeKnown !== 'true') {
        event.stopPropagation();
        const card = addBtn.closest('[data-wb-product-card]');
        const popover = card?.querySelector('[data-wb-card-size-popover]');
        if (popover) {
          this.querySelectorAll('[data-wb-card-size-popover]').forEach(p => {
            if (p !== popover) p.hidden = true;
          });
          popover.hidden = !popover.hidden;
          if (!popover.hidden) {
            this.adjustPopoverPosition(popover);
          }
        }
        return;
      }
      return this.toggleProduct(addBtn);
    }

    const cardSizeBtn = event.target.closest('[data-wb-card-size]');
    if (cardSizeBtn) {
      event.stopPropagation();
      const size = cardSizeBtn.dataset.wbCardSize;
      const card = cardSizeBtn.closest('[data-wb-product-card]');
      const addBtn = card?.querySelector('[data-wb-add]');
      if (addBtn) {
        sessionStorage.setItem('wb-pending-add', addBtn.dataset.productId);
      }
      this.applyGlobalSize(size);
      return;
    }

    const removeBtn = event.target.closest('[data-wb-remove]');
    if (removeBtn) return this.removeProduct(removeBtn.dataset.productId);

    const summary = event.target.closest('[data-wb-toggle-drawer]');
    if (summary) return this.toggleDrawer();

    const changeSize = event.target.closest('[data-wb-change-size]');
    if (changeSize) {
      event.stopPropagation();
      return this.toggleSizePopover();
    }

    const globalSizeOption = event.target.closest('[data-wb-global-size]');
    if (globalSizeOption) return this.applyGlobalSize(globalSizeOption.dataset.wbGlobalSize);

    const checkoutBtn = event.target.closest('[data-wb-checkout]');
    if (checkoutBtn) return this.goToCheckout();

    const cartLink = event.target.closest('[data-wb-goto-cart]');
    if (cartLink) {
      event.preventDefault();
      return this.goToCart(cartLink.href);
    }
  }

  onDocumentClick(event) {
    const popover = this.querySelector('[data-wb-size-popover]');
    if (popover && popover.hasAttribute('data-open')) {
      if (!event.target.closest('.wb-trust-badge')) {
        popover.removeAttribute('data-open');
      }
    }
    if (!event.target.closest('[data-wb-product-card]')) {
      this.querySelectorAll('[data-wb-card-size-popover]').forEach(p => {
        p.hidden = true;
      });
    }
  }

  /* ---------------- Global size selection ---------------- */

  toggleSizePopover() {
    const popover = this.querySelector('[data-wb-size-popover]');
    if (!popover) return;
    if (popover.hasAttribute('data-open')) {
      popover.removeAttribute('data-open');
    } else {
      popover.setAttribute('data-open', '');
    }
  }

  scrollToProducts() {
    const target = this.querySelector('[data-wb-scroll-target]');
    if (!target) return;
    /* Stop short of aligning the size row flush to the very top edge, so it
       lands with clearance below the top of the viewport and stays clearly
       visible (a flush-top or past-top alignment risks pushing it off-screen
       or right against the edge). */
    const clearance = 80;
    const targetY = window.scrollY + target.getBoundingClientRect().top - clearance;
    window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
  }

  /* Every rail is filtered server-side to a single size, so changing size means
     re-rendering the page, not toggling already-loaded cards — a different size
     can surface entirely different products that were never sent to the browser
     in the first place. Same cart-attribute + reload mechanism as the WhatsApp
     order-lookup flow, so both paths stay consistent. */
  async applyGlobalSize(size) {
    const option = this.querySelector(`[data-wb-global-size="${size}"]`);
    if (option) option.disabled = true;
    this.track('Wardrobe Size Changed', { new_size: size });

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { wb_size: size } }),
      });
      window.location.reload();
    } catch (e) {
      if (option) option.disabled = false;
    }
  }

  /* ---------------- Add / remove ---------------- */

  toggleProduct(btn) {
    const productId = btn.dataset.productId;

    if (this.state.has(productId)) {
      this.removeProduct(productId);
      return;
    }

    /* No cap — goal is the MINIMUM to unlock a reward, not a maximum. A
       customer who wants a 4th, 5th, ... shirt just keeps getting a bigger
       reward (see discountPerShirt in connectedCallback). */
    this.state.set(productId, {
      variantId: btn.dataset.variantId,
      title: btn.dataset.title,
      price: btn.dataset.price,
      priceCents: parseInt(btn.dataset.priceCents, 10) || 0,
      image: btn.dataset.image,
      size: btn.dataset.size,
    });
    btn.classList.add('is-added');

    this.persistState();
    this.render(productId);
    this.track('Wardrobe Item Added', {
      product_id: productId,
      product_title: btn.dataset.title,
      price_cents: parseInt(btn.dataset.priceCents, 10) || 0,
      size: btn.dataset.size,
      count_after: this.state.size,
    });

    if (this.state.size === this.goal) {
      this.openDrawer();
    }

    // Real cart sync — see syncAddToCart for why this needs to happen on
    // every add, not just at final checkout.
    this.queueCartSync(productId, () => this.syncAddToCart(productId));
  }

  removeProduct(productId) {
    if (!this.state.has(productId)) return;
    this.state.delete(productId);

    const btn = this.querySelector(`[data-wb-add][data-product-id="${productId}"]`);
    if (btn) btn.classList.remove('is-added');

    this.persistState();
    this.render();
    this.track('Wardrobe Item Removed', { product_id: productId, count_after: this.state.size });

    this.queueCartSync(productId, () => this.syncRemoveFromCart(productId));
  }

  /* ---------------- Rendering ---------------- */

  render(justAddedId) {
    const count = this.state.size;
    const extra = Math.max(0, count - this.goal);

    this.slots.forEach((slot, index) => {
      const filled = index < count;
      slot.classList.toggle('is-filled', filled);
    });

    if (this.slotsExtra) {
      this.slotsExtra.textContent = extra > 0 ? `+${extra}` : '';
      this.slotsExtra.hidden = extra === 0;
    }

    if (justAddedId) {
      const slotIndex = Math.min(count, this.goal) - 1;
      const lastSlot = this.slots[slotIndex];
      const pulseTarget = count > this.goal ? this.slotsExtra : lastSlot;
      if (pulseTarget) {
        pulseTarget.classList.add('is-just-filled');
        pulseTarget.addEventListener('animationend', () => pulseTarget.classList.remove('is-just-filled'), {
          once: true,
        });
      }
    }

    if (this.countLabel) {
      this.countLabel.textContent =
        count <= this.goal ? `${count} of ${this.goal} Shirts Selected` : `${count} Shirts Selected`;
    }

    if (this.teaser) {
      const remaining = this.goal - count;
      if (count === 0) {
        this.teaser.textContent = 'Select your first shirt to get started.';
      } else if (remaining > 0) {
        this.teaser.textContent =
          remaining === 1
            ? "1 more — you're almost there."
            : `${remaining} more to complete your wardrobe.`;
      } else if (this.discountPerShirt > 0) {
        this.teaser.textContent = `Add another for +₹${this.discountPerShirt} more off!`;
      } else {
        this.teaser.textContent = '';
      }
    }

    const complete = count >= this.goal;
    this.toggleAttribute('data-complete', complete);
    if (this.checkoutBtn) this.checkoutBtn.disabled = !complete;

    if (this.celebrationDiscountLabel && this.discountPerShirt > 0) {
      const totalDiscount = this.discountPerShirt * count;
      this.celebrationDiscountLabel.textContent = `₹${totalDiscount} OFF`;
    }

    if (complete && !this.completionTracked) {
      this.completionTracked = true;
      const subtotalCents = Array.from(this.state.values()).reduce((sum, item) => sum + (item.priceCents || 0), 0);
      this.track('Wardrobe Completed', {
        count,
        subtotal_cents: subtotalCents,
        discount_cents: Math.round(this.discountPerShirt * count * 100),
      });
    } else if (!complete) {
      this.completionTracked = false;
    }

    this.renderDrawer();
  }

  renderDrawer() {
    if (!this.drawerItems) return;
    this.drawerItems.innerHTML = '';

    if (this.state.size === 0) {
      const empty = document.createElement('p');
      empty.className = 'wb-drawer__empty';
      empty.textContent = 'Your wardrobe is empty — add a shirt to begin.';
      this.drawerItems.appendChild(empty);
      return;
    }

    this.state.forEach((item, productId) => {
      const row = document.createElement('div');
      row.className = 'wb-drawer__item';
      row.innerHTML = `
        <img src="${item.image}" alt="" width="52" height="52" loading="lazy">
        <div class="wb-drawer__item-info">
          <div class="wb-drawer__item-title">${item.title}</div>
          <div class="wb-drawer__item-price">${item.price}${item.size ? ` · Size ${item.size}` : ''}</div>
        </div>
        <button type="button" class="wb-drawer__item-remove" data-wb-remove data-product-id="${productId}" aria-label="Remove ${item.title}">✕</button>
      `;
      this.drawerItems.appendChild(row);
    });

    /* Only shown once the wardrobe hits the minimum goal — matches the rest of
       the page's "discount is a surprise reveal, not a headline lure"
       approach. Every number here is real: discountPerShirt/upiDiscountPerShirt
       × the actual count, the same formula as the native Shopify automatic
       discount, so this can never show a number that doesn't match checkout —
       and it scales correctly past 3 shirts (3=₹300, 4=₹400, ...). */
    const count = this.state.size;
    if (count >= this.goal && this.discountPerShirt > 0) {
      const subtotalCents = Array.from(this.state.values()).reduce((sum, item) => sum + (item.priceCents || 0), 0);
      const discountCents = Math.round(this.discountPerShirt * count * 100);
      const perShirtOff = this.discountPerShirt;

      const upiPerShirt = this.upiDiscountPerShirt;
      const upiCents = upiPerShirt > 0 ? Math.round(upiPerShirt * count * 100) : 0;

      /* "You pay" is labeled "via UPI" whenever the UPI line is included —
         that total only holds if UPI is actually selected as the payment
         method, so the label has to say so rather than imply it's the price
         for any payment method. */
      const totalCents = Math.max(0, subtotalCents - discountCents - upiCents);
      const totalLabel = upiCents > 0 ? 'You pay via UPI' : 'You pay';

      const summary = document.createElement('div');
      summary.className = 'wb-drawer__summary';
      summary.innerHTML = `
        <div class="wb-drawer__summary-row">
          <span>Subtotal</span>
          <span>${this.formatRupees(subtotalCents)}</span>
        </div>
        <div class="wb-drawer__summary-row wb-drawer__summary-row--discount">
          <span>Wardrobe savings (₹${perShirtOff} off each shirt)</span>
          <span>−${this.formatRupees(discountCents)}</span>
        </div>
        ${
          upiCents > 0
            ? `<div class="wb-drawer__summary-row wb-drawer__summary-row--discount">
                 <span>UPI Offer (₹${upiPerShirt} off each shirt)</span>
                 <span>−${this.formatRupees(upiCents)}</span>
               </div>`
            : ''
        }
        <div class="wb-drawer__summary-row wb-drawer__summary-row--total">
          <span>${totalLabel}</span>
          <span>${this.formatRupees(totalCents)}</span>
        </div>
      `;

      this.drawerItems.appendChild(summary);
    }
  }

  formatRupees(cents) {
    return '₹' + (cents / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }

  toggleDrawer() {
    if (this.hasAttribute('data-drawer-open')) {
      this.closeDrawer();
    } else {
      this.openDrawer();
    }
  }

  openDrawer() {
    const summary = this.querySelector('[data-wb-toggle-drawer]');
    this.setAttribute('data-drawer-open', '');
    if (summary) summary.setAttribute('aria-expanded', 'true');
  }

  closeDrawer() {
    const summary = this.querySelector('[data-wb-toggle-drawer]');
    this.removeAttribute('data-drawer-open');
    if (summary) summary.setAttribute('aria-expanded', 'false');
  }

  /* ---------------- Persistence ---------------- */

  persistState() {
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(Array.from(this.state.entries())));
    } catch (e) {
      /* sessionStorage unavailable (private mode etc.) — selection just won't survive reload */
    }
  }

  restoreState() {
    try {
      const raw = sessionStorage.getItem(this.storageKey);
      if (!raw) return;
      const entries = JSON.parse(raw);
      entries.forEach(([productId, item]) => {
        this.state.set(productId, item);
        const btn = this.querySelector(`[data-wb-add][data-product-id="${productId}"]`);
        if (btn) {
          btn.classList.add('is-added');
          btn.dataset.variantId = item.variantId;
        }
      });
    } catch (e) {
      /* corrupt or blocked storage — start from an empty wardrobe */
    }
  }

  /* ---------------- Cart handoff ----------------
     Selecting a shirt here used to only update local state (Map +
     sessionStorage) — the real Shopify cart was left untouched until
     "Go to cart"/checkout, when clearCart()+addAllToCart() wiped it and
     added the selection in one shot. That meant any OTHER view of the cart
     during the shopping session (e.g. the drawer that opens after adding a
     different item from a product page) genuinely didn't have the
     wardrobe-selected shirt yet, since it was never actually in the cart.

     Now each add/remove syncs to the real cart immediately, tagged with a
     _wardrobe_item line item property so it's identifiable later. Checkout
     still needs to show ONLY the wardrobe selection (not unrelated items
     browsed separately) — reconcileCartForCheckout() handles that by
     stripping non-wardrobe lines right before checkout, instead of
     blindly clearing everything (which would also wipe the wardrobe items
     this file just spent the whole session correctly syncing). */

  queueCartSync(productId, fn) {
    const prev = this.cartSyncQueue.get(productId) || Promise.resolve();
    const next = prev.then(fn).catch((e) => {
      console.warn('Wardrobe cart sync failed', productId, e);
    });
    this.cartSyncQueue.set(productId, next);
    return next;
  }

  async syncAddToCart(productId) {
    const item = this.state.get(productId);
    if (!item) return; // toggled off again before this ran
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{
          id: item.variantId,
          quantity: 1,
          properties: { _wardrobe_item: 'true', _wardrobe_product_id: productId },
        }],
      }),
    });
  }

  async syncRemoveFromCart(productId) {
    const res = await fetch('/cart.js');
    const cart = await res.json();
    const line = cart.items.find((li) => li.properties && li.properties._wardrobe_product_id === productId);
    if (!line) return;
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: line.key, quantity: 0 }),
    });
  }

  /* Called right before "Go to cart" / checkout. Waits for any in-flight
     per-item syncs to settle, then makes the real cart match this.state
     exactly: adds anything selected but somehow still missing (e.g. an
     earlier syncAddToCart failed), removes any wardrobe-tagged line no
     longer selected, and removes every non-wardrobe line so checkout is
     scoped to just this selection. */
  async reconcileCartForCheckout() {
    await Promise.all(Array.from(this.cartSyncQueue.values()));

    const res = await fetch('/cart.js');
    const cart = await res.json();

    const selectedIds = new Set(this.state.keys());
    const cartWardrobeIds = new Set();
    const updates = {};

    cart.items.forEach((line) => {
      const isWardrobeLine = line.properties && line.properties._wardrobe_item === 'true';
      if (isWardrobeLine) {
        const productId = line.properties._wardrobe_product_id;
        if (selectedIds.has(productId)) {
          cartWardrobeIds.add(productId);
          return; // correctly present, leave it alone
        }
      }
      // Not a wardrobe line, or a wardrobe line that's no longer selected.
      updates[line.key] = 0;
    });

    if (Object.keys(updates).length) {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
    }

    const missing = Array.from(this.state.entries())
      .filter(([productId]) => !cartWardrobeIds.has(productId))
      .map(([productId, item]) => ({
        id: item.variantId,
        quantity: 1,
        properties: { _wardrobe_item: 'true', _wardrobe_product_id: productId },
      }));

    if (missing.length) {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: missing }),
      });
    }
  }

  async goToCheckout() {
    /* Checkout goes through GoKwik, not straight to /checkout — see the
       autoTriggerGokwikCheckout IIFE at the top of this file for why a reload
       (via ?wb_checkout=1) is required rather than a direct redirect. The
       wardrobe discount is still code-based (not automatic) — the code
       itself has per-item scaling logic built in (Amount off products, with
       a minimum quantity), so applying it via /discount/[code] still works
       correctly for any quantity, not just exactly 3. */
    if (this.state.size < this.goal || !this.checkoutBtn) return;
    this.checkoutBtn.disabled = true;
    this.checkoutBtn.textContent = 'Going to checkout…';

    const count = this.state.size;
    const subtotalCents = Array.from(this.state.values()).reduce((sum, item) => sum + (item.priceCents || 0), 0);
    this.track('Wardrobe Checkout Started', {
      count,
      subtotal_cents: subtotalCents,
      discount_cents: Math.round(this.discountPerShirt * count * 100),
    });

    try {
      await this.reconcileCartForCheckout();
      const returnPath = `${window.location.pathname}?wb_checkout=1`;
      if (this.discountCode) {
        window.location.href = `/discount/${encodeURIComponent(this.discountCode)}?redirect=${encodeURIComponent(returnPath)}`;
      } else {
        window.location.href = returnPath;
      }
    } catch (e) {
      this.checkoutBtn.disabled = false;
      this.checkoutBtn.textContent = 'Continue to Checkout';
    }
  }

  async goToCart(href) {
    this.track('Wardrobe Go To Cart Clicked', { count: this.state.size });
    // Same reasoning as goToCheckout: the wardrobe discount is code-based,
    // not automatic, so it only shows up on the cart page once the code has
    // actually been redeemed via /discount/[code] — a plain link to /cart
    // adds the items but never redeems the code, which is why the discount
    // was only appearing after going through checkout and back (checkout
    // redeems it, cart doesn't).
    //
    // href here is cartLink.href, a DOM property that always resolves to a
    // FULL ABSOLUTE url (e.g. "https://slay.fashion/cart") even though the
    // markup's href="{{ routes.cart_url }}" attribute is relative. Shopify's
    // /discount/[code]?redirect= silently falls back to the homepage if
    // given that absolute form — it needs a relative path — so it has to be
    // stripped back down to one here.
    let destination = '/cart';
    try {
      const url = new URL(href || '/cart', window.location.origin);
      destination = url.pathname + url.search;
    } catch (e) {
      destination = '/cart';
    }

    try {
      await this.reconcileCartForCheckout();
    } finally {
      if (this.discountCode) {
        window.location.href = `/discount/${encodeURIComponent(this.discountCode)}?redirect=${encodeURIComponent(destination)}`;
      } else {
        window.location.href = destination;
      }
    }
  }
}

customElements.define('wardrobe-builder', WardrobeBuilder);
